import React, { useState } from 'react';
import { FiUser, FiMail, FiShield, FiCalendar, FiEdit, FiSave, FiX, FiCamera, FiActivity, FiClock, FiUsers } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const updateSuccess = await updateUser(formData);

      if (updateSuccess) {
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } catch (err) {
      setError('An error occurred while updating your profile.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card with Gradient */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-primary-600 dark:to-primary-700 rounded-lg dark:rounded-xl shadow-sm dark:shadow-lg overflow-hidden border border-blue-500 dark:border-primary-500/30">
        <div className="px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            {/* Avatar and Info */}
            <div className="flex items-center space-x-6">
              <div className="relative">
                <img
                  src={user.avatar || 'https://ui-avatars.com/api/?name=' + user.firstName + '+' + user.lastName + '&background=3b82f6&color=fff'}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="h-24 w-24 rounded-full border-4 border-white dark:border-primary-400/50 shadow-lg object-cover"
                />
                <button className="absolute bottom-0 right-0 p-2 bg-white dark:bg-dark-400 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors">
                  <FiCamera className="h-4 w-4 text-blue-600 dark:text-primary-400" />
                </button>
              </div>

              <div className="text-white">
                <h1 className="text-2xl font-bold mb-1">
                  {user.firstName} {user.lastName}
                </h1>
                <p className="text-blue-100 dark:text-primary-100 mb-2">@{user.username}</p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white bg-opacity-20 dark:bg-white/10 text-white border border-white/30">
                    <FiShield className="h-3 w-3 mr-1" />
                    {user.role}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white bg-opacity-20 dark:bg-white/10 text-white border border-white/30">
                    <FiCalendar className="h-3 w-3 mr-1" />
                    Joined Jan 2024
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 md:mt-0 flex flex-wrap gap-2">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 bg-white dark:bg-dark-400 text-blue-600 dark:text-primary-400 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-dark-300 transition-colors shadow-sm"
                >
                  <FiEdit className="h-4 w-4 mr-2" />
                  Edit Profile
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-success-600 text-white rounded-lg font-medium hover:bg-green-700 dark:hover:bg-success-700 transition-colors shadow-sm disabled:opacity-50"
                  >
                    <FiSave className="h-4 w-4 mr-2" />
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 dark:bg-white/10 text-white rounded-lg font-medium hover:bg-opacity-30 dark:hover:bg-white/20 transition-colors border border-white/30"
                  >
                    <FiX className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 dark:bg-success-500/20 border border-green-200 dark:border-success-500/30 rounded-lg p-4">
          <div className="text-sm text-green-800 dark:text-success-500">{success}</div>
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-6 hover:shadow-md dark:hover:shadow-xl dark:hover:shadow-primary-500/10 transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Series</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">24</p>
            </div>
            <div className="bg-blue-50 dark:bg-primary-500/20 p-3 rounded-lg border border-blue-200 dark:border-primary-500/30">
              <svg className="h-8 w-8 text-blue-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-6 hover:shadow-md dark:hover:shadow-xl dark:hover:shadow-success-500/10 transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Matches</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">156</p>
            </div>
            <div className="bg-green-50 dark:bg-success-500/20 p-3 rounded-lg border border-green-200 dark:border-success-500/30">
              <FiActivity className="h-8 w-8 text-green-600 dark:text-success-500" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-6 hover:shadow-md dark:hover:shadow-xl dark:hover:shadow-warning-500/10 transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Contests</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">12</p>
            </div>
            <div className="bg-orange-50 dark:bg-warning-500/20 p-3 rounded-lg border border-orange-200 dark:border-warning-500/30">
              <FiClock className="h-8 w-8 text-orange-600 dark:text-warning-500" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg p-6 hover:shadow-md dark:hover:shadow-xl dark:hover:shadow-secondary-500/10 transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">1,247</p>
            </div>
            <div className="bg-purple-50 dark:bg-secondary-500/20 p-3 rounded-lg border border-purple-200 dark:border-secondary-500/30">
              <FiUsers className="h-8 w-8 text-purple-600 dark:text-secondary-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Profile Information Card */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-lg dark:rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Profile Information</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FiUser className="inline h-4 w-4 mr-1" />
                First Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent"
                />
              ) : (
                <p className="text-base text-gray-900 dark:text-white py-2">{user.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FiUser className="inline h-4 w-4 mr-1" />
                Last Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent"
                />
              ) : (
                <p className="text-base text-gray-900 dark:text-white py-2">{user.lastName}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FiMail className="inline h-4 w-4 mr-1" />
                Email Address
              </label>
              {isEditing ? (
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-500 focus:border-transparent"
                />
              ) : (
                <p className="text-base text-gray-900 dark:text-white py-2">{user.email}</p>
              )}
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FiUser className="inline h-4 w-4 mr-1" />
                Username
              </label>
              <p className="text-base text-gray-600 dark:text-gray-400 py-2">{user.username}</p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Username cannot be changed</p>
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FiShield className="inline h-4 w-4 mr-1" />
                Role
              </label>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 dark:bg-primary-500/20 text-blue-800 dark:text-primary-400 border border-blue-200 dark:border-primary-500/30">
                {user.role}
              </span>
            </div>

            {/* Account Created */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FiCalendar className="inline h-4 w-4 mr-1" />
                Account Created
              </label>
              <p className="text-base text-gray-900 dark:text-white py-2">January 2024</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
